import {
  after,
  cloneDeepWith,
  find,
  flatMap,
  isPlainObject,
  isUndefined,
  keyBy,
  mapValues,
  omit,
  isEmpty,
  map,
  pick,
  filter,
  findIndex,
} from 'lodash';
import { v1 as uuid } from 'uuid';
import { defaultAges } from 'containers/TargetingPage/Criteria/AgeTargetingCard/helpers';
import { defaultGenders } from 'containers/TargetingPage/Criteria/GenderTargetingCard/helpers';
import { defaultCountries } from 'containers/TargetingPage/Criteria/TargetingCriterionCard/helpers';
import {
  menuItems,
  initialCriteria,
  COMMON_AUDIENCE_CRITERIA,
  geolocationCriteriaKeysWithoutPostalCode,
} from './constants';
import { setupCriterion } from './Criteria/helpers';

export const getEmptyScreeningQuestion = () => ({
  id: undefined,
  isScreening: true,
  prompt: '',
  isEnabled: false,
  type: 0,
  shuffle: 0,
  scrollIntoView: true,
  answers: [],
});

export const allMenuCriteriaOrder = mapValues(
  keyBy(flatMap(menuItems, 'criteria'), 'key'),
  'order',
);

export const getEnabledCommonCriteria = ({ criteria }) =>
  criteria.reduce((accumulator, criterion) => {
    if (COMMON_AUDIENCE_CRITERIA.includes(criterion.key) && criterion.values) {
      accumulator[criterion.key] = {
        isEnabled: criterion.isEnabled,
        values: criterion.values,
      };
    }

    return accumulator;
  }, {});

export const createAudience = (
  remainingCompletes,
  surveyIsInternal,
  defaultAudience,
  userHideAgeUnderEighteen,
) => {
  const commonCriteriaEnabled = getEnabledCommonCriteria(defaultAudience);
  const criteria = initialCriteria.map((criterion) => {
    let specificInitialValues = {};
    if (criterion.key === 'age') {
      specificInitialValues = {
        isEnabled: true,
        values: defaultAges(userHideAgeUnderEighteen),
        refId: uuid(),
        hasQuotas: false,
        isPristine: true,
      };
    } else if (criterion.key === 'gender') {
      specificInitialValues = {
        isEnabled: true,
        values: filter(defaultGenders(), 'enabled'),
        refId: uuid(),
        hasQuotas: false,
        isPristine: true,
      };
    } else if (criterion.key === 'api_key' && surveyIsInternal) {
      specificInitialValues = {
        isEnabled: true,
        refId: uuid(),
        hasQuotas: false,
        label: 'Apps',
        isDisablingAllowed: false,
        isRequired: true,
        isPristine: true,
      };
    } else if (criterion.key === 'country') {
      specificInitialValues = {
        isEnabled: true,
        hasQuotas: false,
        values: defaultCountries(),
      };
    } else if (commonCriteriaEnabled[criterion.key]) {
      const commonCriterion = commonCriteriaEnabled[criterion.key];

      specificInitialValues = {
        isEnabled: commonCriterion.isEnabled,
        hasQuotas: false,
        values: commonCriterion.values.map((item) => ({
          ...item,
          refId: uuid(),
          id: undefined,
          isTemporary: undefined,
        })),
      };
    }
    const updatedCriterion = {
      ...criterion,
      ...specificInitialValues,
    };
    const initializedCriterion = setupCriterion(updatedCriterion);
    return initializedCriterion;
  });

  return {
    refId: uuid(),
    criteria,
    quotaCompletes: remainingCompletes,
  };
};

export const duplicateAudience = (audience) =>
  cloneDeepWith(audience, function customizer(value, key) {
    // remove id properties as those are always from the DB
    if (isPlainObject(value)) {
      return cloneDeepWith(omit(value, 'id'), after(2, customizer));
    }
    // create new refId when found
    if (key === 'refId') return uuid();

    return undefined;
  });

export const getMobileOsEnabled = ({
  selectedAccountHasMobileOsPerk,
  audience,
}) =>
  selectedAccountHasMobileOsPerk &&
  !isUndefined(
    find(audience.criteria, { key: 'has_os_version', isEnabled: true }),
  );

export const featureCleaner = (criteriaList) =>
  criteriaList
    .filter(({ values }) => !isEmpty(values))
    .map(({ key, values, hasQuotas }) => {
      const cleanValueKeys = ['value', 'quotaCompletes', 'id', 'refId'];
      if (key === 'api_key' || key === 'latlon') {
        cleanValueKeys.push('label');
      }
      const cleanValues = map(values, (value) => pick(value, cleanValueKeys));

      return {
        key,
        hasQuotas,
        values: cleanValues,
      };
    });
export const getActiveScreeningQuestionCount = (screeningQuestions = []) =>
  screeningQuestions.filter(({ isScreenerActive }) => isScreenerActive).length;

export const getScreeningQuestionsAfterAdd = (screeningQuestions, payload) => {
  const { newScreeningQuestion, insertAfterQuestionRefId } = payload;
  const newNodes = [...screeningQuestions];
  if (!insertAfterQuestionRefId) {
    newNodes.push(newScreeningQuestion);
  } else {
    const offset = 1;
    const index = insertAfterQuestionRefId
      ? findIndex(screeningQuestions, { refId: insertAfterQuestionRefId }) +
        offset
      : screeningQuestions.length;

    newNodes.splice(index, 0, newScreeningQuestion);
  }
  return newNodes;
};

export const getScreeningQuestionsAfterRemove = (
  screeningQuestions,
  removedQuestionRefId,
) => screeningQuestions.filter(({ refId }) => refId !== removedQuestionRefId);

export const getScreeningQuestionsAfterUpdate = (
  screeningQuestions,
  updatedQuestion,
) => {
  const sanitisedQuestion = { ...updatedQuestion };
  /*
      Temporarily valid answers (valid only for storing to DB as part of autosave) are considered those with body or image.
      We also added those with an id (from the DB), regardless of value, so that they do not get deleted
      while user is editing them. (alternatively we could remove the id when the body is empty)

      Current implementation will cause backend to crash if we do not allow answers with id to pass
      as the backend will remove the record if the id is not posted, and once the user will refill the answer
      the UI will re-use the id and the backend will crash because it will try to update a not existing record
    */
  sanitisedQuestion.answers = filter(
    sanitisedQuestion.answers,
    ({ body, media, id }) => body || id || media,
  );

  return screeningQuestions.map((question) => {
    if (question.refId === sanitisedQuestion.refId) {
      return sanitisedQuestion;
    }
    return question;
  });
};

export const hasGeolocationCriteriaEnabled = (
  audiences,
  activeAudienceRefId,
) => {
  const audience = audiences.find(({ refId }) => refId === activeAudienceRefId);

  return audience.criteria.some(
    (criterion) =>
      geolocationCriteriaKeysWithoutPostalCode.includes(criterion.key) &&
      criterion.isEnabled,
  );
};
